<template>
    <div v-if="$page.props.auth.check">
        <dialog-modal :show="show" :closeable="false" max-width="xl" @close="close">
            <template #content>
                <div class="-mb-2">
                    <div class="flex items-center justify-end -mr-2 -mt-2">
                        <modal-close @close="close" />
                    </div>

                    <div class="grid gap-2 grid-cols-7 mt-2">
                        <div class="col-span-7 text-center">
                            <label class="block text-gray-700 dark:text-slate-400 text-2xl">{{ options.title }}</label>
                            <p v-if="minimumPledgeAmount === 5" class="my-2 text-gray-600 dark:text-slate-400 text-sm font-medium tracking-wide">{{ options.description }}</p>
                            <div v-if="minimumPledgeAmount === 0" class="my-2 pt-2 px-6 text-gray-600 dark:text-slate-400 text-base text-left font-medium tracking-wide" v-html="options.description"></div>
                        </div>

                        <div class="col-span-7 text-center rounded-md sm:col-span-5 sm:col-start-2">
                            <div class="flex mt-1 rounded-md">
                                <button
                                    v-long-press="decreaseAmountByTen"
                                    class="prevent-zoom-button relative inline-flex items-center -mr-px px-4 py-2 hover:text-blue-700 text-gray-600 dark:text-slate-400 dark:hover:text-slate-100 font-medium hover:bg-gray-100 bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800/50 border active:border-blue-500 border-gray-300 rounded-l-md focus:outline-none space-x-2 focus:ring-1 active:ring-blue-500"
                                    @click="decreaseAmount"
                                >
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 12H6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                    </svg>
                                </button>
                                <div class="focus-within:z-10">
                                    <input
                                        v-model="form.amount"
                                        :class="[isOverlimit ? 'text-red-500 focus:ring-red-500 focus:border-red-500' : 'focus:ring-indigo-500 focus:border-indigo-500', amountLength > 4 ? 'text-3xl lg:text-5xl' : 'text-5xl']"
                                        class="block w-full text-center border-gray-300 dark:border-slate-300 focus:border-indigo-500 rounded-none focus:ring-indigo-500"
                                        placeholder="5"
                                        type="number"
                                        @blur="validateAmount"
                                    />
                                </div>
                                <button
                                    v-long-press="increaseAmountByTen"
                                    class="prevent-zoom-button relative inline-flex items-center -ml-px px-4 py-2 hover:text-blue-700 text-gray-600 dark:text-slate-400 dark:hover:text-slate-100 font-medium hover:bg-gray-100 bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800/50 border active:border-blue-500 border-gray-300 rounded-r-md focus:outline-none space-x-2 focus:ring-1 active:ring-blue-500"
                                    @click="increaseAmount"
                                >
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="flex flex-col col-span-7 text-center text-gray-500 text-xs xl:text-base">
                            <div v-if="isOverlimit" class="my-1.5 text-red-500 text-xs font-bold uppercase">You do not have enough tokens</div>
                            <div v-show="$page.props.user.wallet.balance" class="has-tooltip">
                                <button :class="{ 'font-bold': isOverlimit }" class="px-2.5 py-2 text-blue-500 dark:text-white hover:text-blue-700 dark:hover:text-blue-500 focus:outline-none uppercase" type="button" @click="setMaxTokens">
                                    Pledge up to {{ $page.props.user.wallet.balance | toLocaleString }} tokens
                                </button>
                                <tooltip>Pledge All</tooltip>
                            </div>
                            <div v-if="!isOverlimit" class="flex">
                                <hr class="flex-grow my-3 border-gray-300 dark:border-slate-400" />
                                <span class="flex-grow-0 place-self-center px-4 text-gray-500 dark:text-slate-400 text-xs uppercase">or</span>
                                <hr class="flex-grow my-3 border-gray-300 dark:border-slate-400" />
                            </div>
                            <button v-if="!isOverlimit" class="px-2.5 py-2 text-blue-500 dark:text-white hover:text-blue-700 dark:hover:text-blue-500 focus:outline-none uppercase" @click="closeAndBuyTokens">Buy More Tokens</button>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex items-center justify-between align-middle w-full">
                    <span v-if="!isOverlimit" class="flex align-middle sm:mr-auto">
                        <toggle-right-label v-model="form.anonymous">Be Anonymous</toggle-right-label>
                    </span>

                    <jet-button v-if="!isOverlimit && form.amount > 0" :processing="form.processing" class="justify-centerdisabled:bg-brand-300 bg-brand-500 hover:bg-brand-700 active:bg-brand-700 ring-brand-700" @click.native="closeAndShowTerms"> Pledge </jet-button>
                    <jet-button v-if="!isPledging" :processing="form.processing" class="justify-center" @click.native="close"> Close </jet-button>

                    <jet-button v-if="isOverlimit" :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="justify-center ml-auto disabled:bg-brand-300 bg-brand-500 hover:bg-brand-700 active:bg-brand-700 ring-brand-700" @click.native="closeAndBuyTokens">
                        <span v-if="amountDiff >= minimumPurchaseAmount">Buy {{ amountDiff | toLocaleString }} Tokens</span>
                        <span v-else>Buy More Tokens</span>
                    </jet-button>
                </div>
            </template>
        </dialog-modal>

        <!-- Terms Modal -->
        <dialog-modal :show="showingTermsModal">
            <template #content>
                <div class="-mb-2">
                    <div class="flex items-center justify-end -mr-2 -mt-2">
                        <modal-close @close="showingTermsModal = false" />
                    </div>
                    <h2 class="my-2 text-xl font-medium">We Want You To Know...</h2>
                    <p class="my-2 text-gray-800 text-sm">
                        We make every effort to review challenges to the best of our abilities. However, due to the nature of challenges, we cannot guarantee their validity. As a result, we are not responsible for any inaccurate, fake, or misrepresented content or submissions.
                        <!--If you have any questions, you may <button class="inline-flex text-blue-600" @click="contactSupport">contact</button> our support team.-->
                    </p>
                    <p class="my-2 text-gray-800 text-sm">
                        Challenges remain active for a duration of 120 days or until they are completed. After this initial period, the
                        <span class="has-tooltip inline-block">
                            <abbr class="hover:text-yellow-600 text-yellow-700 cursor-help">Challenger</abbr>
                            <tooltip>Person who creates the challenge</tooltip>
                        </span>
                        has two options: they can choose to extend the challenge for an additional 120 days, or they can archive the challenge. If the challenge is archived, the tokens you pledged will be returned to your token balance.
                    </p>
                    <div class="my-2 text-gray-800 text-sm">
                        If a challenge is not completed, as a pledger, you have three options regarding your tokens:
                        <ol class="list-decimal ml-4 my-4 flex flex-col gap-2">
                            <li>Your tokens will be returned to your wallet, allowing you to pledge them to a different challenge.</li>
                            <li>Your tokens can be returned to your wallet, giving you the opportunity to use them to create new challenges.</li>
                            <li>Your tokens will be returned to your wallet, and you can keep them there until you're ready to use them.</li>
                        </ol>
                        These options ensure that you have full control over your tokens and can decide how best to support or initiate challenges within our community.
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="relative hidden items-center mr-auto">
                    <div class="flex items-center h-5">
                        <input id="dont_show_pledge_terms" v-model="form.dont_show_pledge_terms" aria-describedby="dont-show-pledge-terms" class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" type="checkbox" />
                    </div>
                    <div class="ml-3 text-sm">
                        <label class="text-gray-600 font-medium" for="dont_show_pledge_terms">Never show this message again.</label>
                    </div>
                </div>
                <div class="flex items-center">
                    <!--<button class="text-gray-500 text-xs uppercase" @click="showingTermsModal = false">Nevermind</button>-->
                    <jet-button class="ml-6 disabled:bg-brand-300 bg-brand-500 hover:bg-brand-700 active:bg-brand-700 ring-brand-700" @click.native="pledgeTokens">I Understand</jet-button>
                </div>
            </template>
        </dialog-modal>
    </div>
</template>

<script>
import Modal from "@/Jetstream/Modal"
import DialogModal from "@/Jetstream/DialogModal"
import JetButton from "@/Jetstream/Button"
import JetLabel from "@/Jetstream/Label"
import JetInput from "@/Jetstream/Input"
import JetInputError from "@/Jetstream/InputError"
import { LongPress } from "@/Directives/LongPress"
import ToggleRightLabel from "@/Components/ToggleRightLabel"
import ModalClose from "@/Components/ModalClose"
import Tooltip from "@/Components/Tooltip"
import JetSecondaryButton from "@/Jetstream/SecondaryButton"

export default {
    name: "PledgeTokensModal",

    directives: {
        "long-press": LongPress,
    },

    components: {
        Tooltip,
        ModalClose,
        ToggleRightLabel,
        JetInputError,
        JetInput,
        JetLabel,
        JetButton,
        DialogModal,
        Modal,
        JetSecondaryButton,
    },

    mounted() {
        this.preventZoomOnButtons()
        if (this.$attrs.options) {
            this.options = this.$attrs.options

            // Update the minimumPledgeAmount if it exists in this.$attrs.options
            if (this.$attrs.options.minimumPledgeAmount) {
                this.minimumPledgeAmount = this.$attrs.options.minimumPledgeAmount
            }
        }
        // Adjust form.amount based on wallet balance condition
        if (this.$page.props.user.wallet.balance < 10) {
            this.form.amount = this.$page.props.user.wallet.balance
        }
    },

    props: {
        show: {
            default: false,
            type: Boolean,
        },
        processing: {
            default: false,
            type: Boolean,
        },
    },

    watch: {
        show(bool) {
            if (bool) {
                this.verifyAuth().then((auth) => {
                    if (auth) {
                        this.showModal = bool
                    } else {
                        this.$inertia.get(route("login"))
                    }
                })
            }
        },

        // Up to one million
        "form.amount": function (amount) {
            if (this.amountLength <= 8) {
                this.form.amount = this.form.amount.toString().substring(0, 7)
            }
        },

        "$page.props.user.wallet.balance": function (newBalance) {
            if (newBalance < 10) {
                this.form.amount = newBalance
            }
        },
    },

    computed: {
        isOverlimit() {
            return this.form.amount > this.$page.props.user.wallet.balance
        },

        amountDiff() {
            return this.form.amount - this.$page.props.user.wallet.balance
        },

        amountLength() {
            return this.form.amount.toString().length
        },

        /** Convert difference to positive number **/
        remainingBalance() {
            if (this.amountDiff < 0) {
                return Math.abs(this.amountDiff)
            }
        },

        postId() {
            return this.$page.props.post.id
        },

        isPledging() {
            return this.form.amount > 0
        },
    },

    data() {
        return {
            minimumPurchaseAmount: 5,
            minimumPledgeAmount: 0,
            step: 5,
            form: this.$inertia.form({
                _method: "PUT",
                amount: 10,
                name: null,
                cc_number: null,
                anonymous: false,
                dont_show_pledge_terms: false,
            }),
            showingTermsModal: false,
            options: {
                show: false,
                title: "Make A Pledge",
                description: "How many tokens do you want to pledge?",
            },
            lastButtonTouch: 0,
        }
    },

    methods: {
        preventZoomOnButtons() {
            const buttons = document.querySelectorAll(".prevent-zoom-button") // Use your actual button class or ID
            buttons.forEach((button) => {
                button.addEventListener(
                    "touchstart",
                    (e) => {
                        e.preventDefault() // Prevents zoom but also other default actions
                        // Manually trigger the button's click event or desired function
                        button.click() // Or this.yourMethodName() if you have specific logic to execute
                    },
                    { passive: false }
                ) // Ensure the listener can call preventDefault
            })
        },
        handleButtonTouch(elements) {
            const now = Date.now()
            const DOUBLE_TAP_THRESHOLD = 300 // milliseconds, increased from 300ms
            if (now - this.lastButtonTouch <= DOUBLE_TAP_THRESHOLD) {
                // Detected a double-tap, prevent further action
                return true // Indicate that a double-tap was detected
            }
            this.lastButtonTouch = now
            return false // No double-tap detected
        },

        pledgeTokens() {
            this.showingTermsModal = false
            this.form.post(route("wallet.pledge.store", { post: this.postId }), {
                onSuccess: () => this.form.reset(),
                onFinish: () => this.close(),
                resetOnSuccess: true,
                preserveScroll: true,
            })
        },

        close() {
            this.$emit("update:show", false)
        },

        increaseAmount() {
            if (this.handleButtonTouch()) return
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value += 5
            this.form.amount = value
        },

        increaseAmountByTen() {
            if (this.handleButtonTouch()) return
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value += 10
            this.form.amount = value
        },

        decreaseAmount() {
            if (this.handleButtonTouch()) return
            if (this.form.amount <= this.minimumPledgeAmount) return false
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value < 1 ? (value = 1) : ""
            value -= 5
            this.form.amount = value
        },

        decreaseAmountByTen() {
            if (this.handleButtonTouch()) return
            if (this.form.amount <= this.minimumPledgeAmount) return false
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value < 1 ? (value = 1) : ""
            value -= 10
            this.form.amount = value
        },

        validateAmount() {
            if (this.form.amount < this.minimumPledgeAmount) {
                this.form.amount = this.minimumPledgeAmount
            }
            // Ensure form.amount does not exceed the wallet balance if balance is less than 10
            // if (this.$page.props.user.wallet.balance < 10) {
            //     this.form.amount = this.$page.props.user.wallet.balance;
            // }
        },

        setMaxTokens() {
            this.form.amount = this.$page.props.user.wallet.balance
        },

        closeAndShowTerms() {
            this.close()
            this.showTermsModal()
        },

        closeAndBuyTokens() {
            this.close()
            this.buyTokens()
        },

        showTermsModal: _.debounce(function () {
            this.showingTermsModal = true
            // if (!this.$page.props.user.settings.dont_show_pledge_terms) {
            //     this.showingTermsModal = true
            // } else {
            //     this.pledgeTokens()
            // }
        }, 50),

        buyTokens: _.debounce(function () {
            // this.preserveBackdrop = true
            this.$root.$emit("showBuyTokensModal", this.amountDiff)
        }, 50),
    },
}
</script>
